import { ConversationLanguageCode } from 'lib-shared-languages';

import { ConversationLinkType, User } from '../../index';
import { ConversationRatingEligibilityEvaluationResults } from '../../satisfaction';

/** Represents the activity log type. */
export type ActivityType =
  ConversationActivityLog['activity_log'][number]['activity_type'];

/** Represents an activity log entry. */
export type ConversationActivityLogEntry =
  | ConversationAssignedActivityLogEntry
  | ConversationAutoreplySentActivityLogEntry
  | ConversationClaimActivityLogEntry
  | ConversationClosedActivityLogEntry
  | ConversationCreatedLogEntry
  | ConversationFollowupLogEntry
  | ConversationLanguageUpdatedLogEntry
  | ConversationMessageAddedLogEntry
  | ConversationNoteAddedLogEntry
  | ConversationOfferAcceptedLogEntry
  | ConversationOfferedLogEntry
  | ConversationOfferRejectedLogEntry
  | ConversationOfferTimeoutLogEntry
  | ConversationPendingLogEntry
  | ConversationRatedLogEntry
  | ConversationRatingCancelledLogEntry
  | ConversationRatingOfferedLogEntry
  | ConversationRatingNotOfferedLogEntry
  | ConversationRatingScheduledLogEntry
  | ConversationRatingUnscheduledLogEntry
  | ConversationReopenedLogEntry
  | ConversationReservedLogEntry
  | ConversationTagLogEntry
  | ConversationTransferLogEntry
  | ConversationUnassignedLogEntry
  | ConversationEndUserReplacedLogEntry
  | ConversationAutomationLogEntry
  | ConversationLinkLogEntry
  | ConversationSlaEvaluationLogEntry;

/** Represents an activity log user. */
type ActivityLogUser = Pick<User, 'id' | 'name' | 'email' | 'phone_number'>;

/** Represents the activity log entry base model. */
type ActivityLogEntryBase<
  TEntryType extends string,
  TEntryEntity extends Record<string, unknown> = Record<string, unknown>,
> = TEntryEntity & {
  /** The id of the event entry. */
  readonly event_id: string;
  /** The timestamp of the event entry. */
  readonly event_timestamp: string;
  /** The id of the conversation. */
  readonly csid: number;
  /** The type of entry. */
  readonly activity_type: TEntryType;
  /** The author. */
  readonly author: ActivityLogUser | null;
};

/** Represents the conversation activity log model. */
export type ConversationActivityLog = {
  /** The activity log entries. */
  readonly activity_log: readonly ConversationActivityLogEntry[];
};

/** Represents the conversation agent assigned activity log entry model. */
export type ConversationAssignedActivityLogEntry = ActivityLogEntryBase<
  'CONVERSATION_ASSIGNED',
  {
    /** The id of the agent. */
    readonly agent_id: string;
    /** The name of the agent. */
    readonly agent_name: string;
  }
>;

/** Represents the conversation autoreply sent activity log entry model. */
export type ConversationAutoreplySentActivityLogEntry = ActivityLogEntryBase<
  'CONVERSATION_AUTOREPLY_SENT',
  {
    /** The autoreply template name. */
    readonly template_name: string;
  }
>;

/** Represents the conversation claim activity log entry model. */
export type ConversationClaimActivityLogEntry = ActivityLogEntryBase<
  'CONVERSATION_CLAIMED',
  {
    /** The name from which the conversation was claimed. */
    readonly claimed_from_label: string;
    /** The type the conversation was claimed from. */
    readonly claimed_from_type: 'QUEUE';
  }
>;

/** Represents the conversation closed activity log entry model. */
export type ConversationClosedActivityLogEntry =
  ActivityLogEntryBase<'CONVERSATION_CLOSED'>;

/** Represents the conversation created activity log entry model. */
export type ConversationCreatedLogEntry = ActivityLogEntryBase<
  'CONVERSATION_CREATED_BY_AGENT' | 'CONVERSATION_CREATED_BY_CUSTOMER',
  {
    /** The subject. */
    readonly conversation_subject: string | null;
  }
>;

/** Represents the conversation language updated activity log entry model. */
export type ConversationLanguageUpdatedLogEntry = ActivityLogEntryBase<
  'CONVERSATION_LANGUAGE_UPDATED',
  {
    /** The language code. */
    readonly language: ConversationLanguageCode;
  }
>;

/** Represents the conversation offer accepted activity log entry model. */
export type ConversationOfferAcceptedLogEntry =
  ActivityLogEntryBase<'CONVERSATION_OFFER_ACCEPTED'>;

/** Represents the conversation offer rejected activity log entry model. */
export type ConversationOfferRejectedLogEntry =
  ActivityLogEntryBase<'CONVERSATION_OFFER_REJECTED'>;

/** Represents the conversation offer timeout activity log entry model. */
export type ConversationOfferTimeoutLogEntry =
  ActivityLogEntryBase<'CONVERSATION_OFFER_TIMEOUT'>;

/** Represents the conversation offered activity log entry model. */
export type ConversationOfferedLogEntry = ActivityLogEntryBase<
  'CONVERSATION_OFFERED',
  {
    /** The names of the agents. */
    readonly agent_names: readonly string[];
    /** The name of the queue. */
    readonly queue_label: string;
  }
>;

/** Represents the conversation pending activity log entry model. */
export type ConversationPendingLogEntry =
  ActivityLogEntryBase<'CONVERSATION_PENDING'>;

/** Represents the conversation rated activity log entry model. */
export type ConversationRatedLogEntry = ActivityLogEntryBase<
  'CONVERSATION_RATED',
  {
    /** The numeric score. */
    readonly rating_score: string;
    /** The message. */
    readonly rating_message: string | null;
    /** The agent. */
    readonly agent: ActivityLogUser;
  }
>;

/** Represents the conversation rating cancelled activity log entry model. */
export type ConversationRatingCancelledLogEntry =
  ActivityLogEntryBase<'CONVERSATION_RATING_CANCELLED'>;

/** Represents the conversation rating offered activity log entry model. */
export type ConversationRatingOfferedLogEntry = ActivityLogEntryBase<
  'CONVERSATION_RATING_OFFERED',
  {
    /** The agent. */
    readonly agent: ActivityLogUser;
    /** The user. */
    readonly user: ActivityLogUser;
    /** The filter evaluation results. */
    readonly evaluation_filters?: ConversationRatingEligibilityEvaluationResults;
  }
>;

/**
 * Represents the conversation rating not offered activity log entry model. The
 * payload contains the filter evaluation results which can be used determine
 * why the rating was not offered.
 */
export type ConversationRatingNotOfferedLogEntry = ActivityLogEntryBase<
  'CONVERSATION_RATING_NOT_OFFERED',
  {
    evaluation_filters: ConversationRatingEligibilityEvaluationResults;
  }
>;

/** Represents the conversation rating scheduled activity log entry model. */
export type ConversationRatingScheduledLogEntry = ActivityLogEntryBase<
  'CONVERSATION_RATING_SCHEDULED',
  {
    /** The timestamp for when the csat should be sent. */
    readonly rating_scheduled_time: string;
  }
>;

/** Represents the conversation rating unscheduled activity log entry model. */
export type ConversationRatingUnscheduledLogEntry =
  ActivityLogEntryBase<'CONVERSATION_RATING_UNSCHEDULED'>;

/** Represents the conversation reopened activity log entry model. */
export type ConversationReopenedLogEntry =
  ActivityLogEntryBase<'CONVERSATION_REOPENED'>;

/** Represents the conversation reopened activity log entry model. */
export type ConversationReservedLogEntry = ActivityLogEntryBase<
  'CONVERSATION_RESERVED',
  {
    /** The agent. */
    readonly agent: ActivityLogUser;
    /** The queue id. */
    readonly queue_id: string;
    /** The queue name. */
    readonly queue_name: string;
    /** The reservation type. */
    readonly reservation_type:
      | 'FOLLOW_UP'
      | 'LAST_AGENT'
      | 'PREFERRED_AGENT'
      | 'UNKNOWN';
    /** The timestamp for when this reservation expires. */
    readonly valid_until: string;
  }
>;

/** Represents the conversation unassigned activity log entry model. */
export type ConversationUnassignedLogEntry = ActivityLogEntryBase<
  'CONVERSATION_UNASSIGNED',
  {
    /** The agent. */
    readonly agent: ActivityLogUser;
  }
>;

/** Represents the conversation followup activity log entry model. */
export type ConversationFollowupLogEntry = ActivityLogEntryBase<
  'FOLLOWUP_ADDED' | 'FOLLOWUP_REMOVED' | 'FOLLOWUP_EXPIRED'
>;

/** Represents the conversation message added activity log entry model. */
export type ConversationMessageAddedLogEntry = ActivityLogEntryBase<
  'MESSAGE_ADDED_BY_AGENT' | 'MESSAGE_ADDED_BY_CUSTOMER',
  {
    /** The id of the message. */
    readonly message_id: string;
    /** The url to the author's avatar. */
    readonly author_avatar_url: string;
    /** The endpoint email that added the message. */
    readonly from_endpoint: string;
  }
>;

/** Represents the conversation note added activity log entry model. */
export type ConversationNoteAddedLogEntry = ActivityLogEntryBase<
  'NOTE_ADDED',
  {
    /** The url to the agent's avatar. */
    readonly author_avatar_url: string;
    /** The message */
    readonly message: string;
  }
>;

/** Represents the conversation tag activity log entry model. */
export type ConversationTagLogEntry = ActivityLogEntryBase<
  'TAG_ADDED' | 'TAG_REMOVED',
  {
    /** The name of the tag. */
    readonly tag_name: string;
  }
>;

/** Represents the conversation link activity log entry model. */
export type ConversationLinkLogEntry = ActivityLogEntryBase<
  | 'CONVERSATION_LINKED_FROM_CHILD'
  | 'CONVERSATION_UNLINKED_FROM_CHILD'
  | 'CONVERSATION_LINKED_TO_PARENT'
  | 'CONVERSATION_UNLINKED_TO_PARENT',
  {
    /** The child conversation id. */
    readonly child: number;
    /** The parent conversation id. */
    readonly parent: number;
    /** The type of conversation link. */
    readonly link_type: ConversationLinkType;
  }
>;

/** Represents the conversation transfer activity log entry model. */
export type ConversationTransferLogEntry = ActivityLogEntryBase<
  'TRANSFER_INITIATED' | 'TRANSFER_SUCCESSFUL' | 'TRANSFER_FAILED',
  {
    /** The id of the destination. */
    readonly destination_id: string;
    /** The name of the destination. */
    readonly destination_label: string;
    /** The type of destination. */
    readonly destination_type: 'QUEUE' | 'AGENT';
    /** The reason for transfer. */
    readonly reason: string | null;
    /** The type of transfer. */
    readonly transfer_type: 'BLIND';
  }
>;

/** Represents the SLA evaluation activity log entry model. */
export type ConversationSlaEvaluationLogEntry = ActivityLogEntryBase<
  'CONVERSATION_SLA_EVALUATION',
  {
    /** The status of the SLA evaluation. */
    readonly status: 'Pending' | 'Complied' | 'Breached';
    /** The id of the SLA policy. */
    readonly slaPolicyId: number;
    /** The name of the SLA policy. */
    readonly slaPolicyName: string;
    /** The type of SLA metric. */
    readonly slaMetricType: 'Response_Time';
    /** The timestamp when the SLA was complied with. */
    readonly compliedAt: string | null;
    /** The timestamp when the SLA breach is due. */
    readonly breachDueAt: string | null;
  }
>;

/** Represents the conversation end user replaced activity log entry model. */
export type ConversationEndUserReplacedLogEntry = ActivityLogEntryBase<
  'CONVERSATION_END_USER_REPLACED',
  {
    /** The previous end user. */
    readonly old_user: User;
    /** The new end user. */
    readonly new_user: User;
    /** The reason that the end user was replaced */
    readonly reason: 'AUTH_UPGRADE_ANONYMOUS_TO_CLAIMED' | 'CONTACTS_MERGED';
  }
>;

export enum AutomationActivityType {
  AUTOMATION_STARTED = 'AUTOMATION_STARTED',
  AUTOMATION_FINISHED = 'AUTOMATION_FINISHED',
  AUTOMATION_CONDITION_NODE_VISITED = 'AUTOMATION_CONDITION_NODE_VISITED',
  AUTOMATION_WAIT_NODE_VISITED = 'AUTOMATION_WAIT_NODE_VISITED',
  AUTOMATION_REPLY_NODE_VISITED = 'AUTOMATION_REPLY_NODE_VISITED',
  AUTOMATION_TAG_NODE_VISITED = 'AUTOMATION_TAG_NODE_VISITED',
  AUTOMATION_QUEUE_NODE_VISITED = 'AUTOMATION_QUEUE_NODE_VISITED',
  AUTOMATION_CLOSE_NODE_VISITED = 'AUTOMATION_CLOSE_NODE_VISITED',
  AUTOMATION_STOP_NODE_VISITED = 'AUTOMATION_STOP_NODE_VISITED',
  AUTOMATION_BUSINESS_HOURS_NODE_VISITED = 'AUTOMATION_BUSINESS_HOURS_NODE_VISITED',
  AUTOMATION_EXTERNAL_INTEGRATION_NODE_VISITED = 'AUTOMATION_EXTERNAL_INTEGRATION_NODE_VISITED',
  AUTOMATION_CONVERSATION_CUSTOM_FIELDS_NODE_VISITED = 'AUTOMATION_CONVERSATION_CUSTOM_FIELDS_NODE_VISITED',
  AUTOMATION_CONTACT_CUSTOM_FIELDS_NODE_VISITED = 'AUTOMATION_CONTACT_CUSTOM_FIELDS_NODE_VISITED',
  AUTOMATION_SIDE_CONVERSATION_NODE_VISITED = 'AUTOMATION_SIDE_CONVERSATION_NODE_VISITED',
  AUTOMATION_THIRD_PARTY_INTEGRATION_NODE_VISITED = 'AUTOMATION_THIRD_PARTY_INTEGRATION_NODE_VISITED',
  AUTOMATION_INTERNAL_NOTE_NODE_VISITED = 'AUTOMATION_INTERNAL_NOTE_NODE_VISITED',
  AUTOMATION_CHATBOT_NODE_VISITED = 'AUTOMATION_CHATBOT_NODE_VISITED',
  AUTOMATION_AI_AGENT_NODE_VISITED = 'AUTOMATION_AI_AGENT_NODE_VISITED',
  AUTOMATION_CHATBOT_SINGLE_INTERACTION_NODE_VISITED = 'AUTOMATION_CHATBOT_SINGLE_INTERACTION_NODE_VISITED',
}

type BaseAutomationActivity = {
  run_id: string;
  csid: number;
  rule_id: string;
  rule_version: string;
  node_id: string;
  next_node_id: string;
  execution_id: number;
};

export type ConversationAutomationConditionLogEntry = ActivityLogEntryBase<
  AutomationActivityType.AUTOMATION_CONDITION_NODE_VISITED,
  BaseAutomationActivity & {
    is_matching: boolean;
  }
>;

export type ConversationAutomationWaitLogEntry = ActivityLogEntryBase<
  AutomationActivityType.AUTOMATION_WAIT_NODE_VISITED,
  BaseAutomationActivity & {
    delay_in_seconds: number;
  }
>;

export type ConversationAutomationReplyLogEntry = ActivityLogEntryBase<
  AutomationActivityType.AUTOMATION_REPLY_NODE_VISITED,
  BaseAutomationActivity
>;

export type ConversationAutomationTagLogEntry = ActivityLogEntryBase<
  AutomationActivityType.AUTOMATION_TAG_NODE_VISITED,
  BaseAutomationActivity & {
    added_tag_ids: string[];
    removed_tag_ids: string[];
  }
>;

export type ConversationAutomationQueueLogEntry = ActivityLogEntryBase<
  AutomationActivityType.AUTOMATION_QUEUE_NODE_VISITED,
  BaseAutomationActivity & {
    queue_id: string;
  }
>;

export type ConversationAutomationCloseLogEntry = ActivityLogEntryBase<
  AutomationActivityType.AUTOMATION_CLOSE_NODE_VISITED,
  BaseAutomationActivity
>;

export type ConversationAutomationStopLogEntry = ActivityLogEntryBase<
  AutomationActivityType.AUTOMATION_STOP_NODE_VISITED,
  BaseAutomationActivity
>;

export type ConversationAutomationBusinessHoursLogEntry = ActivityLogEntryBase<
  AutomationActivityType.AUTOMATION_BUSINESS_HOURS_NODE_VISITED,
  BaseAutomationActivity & {
    schedule_id: string;
  }
>;

export type ConversationAutomationExternalIntegrationLogEntry =
  ActivityLogEntryBase<
    AutomationActivityType.AUTOMATION_EXTERNAL_INTEGRATION_NODE_VISITED,
    BaseAutomationActivity & {
      outcome: Outcome;
    }
  >;

export type ConversationAutomationFinishedLogEntry = ActivityLogEntryBase<
  AutomationActivityType.AUTOMATION_FINISHED,
  BaseAutomationActivity
>;

export type ConversationAutomationSideConversationLogEntry =
  ActivityLogEntryBase<
    AutomationActivityType.AUTOMATION_SIDE_CONVERSATION_NODE_VISITED,
    BaseAutomationActivity & {
      csid_created: number | null;
      queue_id: string | null;
      tag_ids: string[];
    }
  >;

export type ConversationAutomationThirdPartyIntegrationLogEntry =
  ActivityLogEntryBase<
    AutomationActivityType.AUTOMATION_THIRD_PARTY_INTEGRATION_NODE_VISITED,
    BaseAutomationActivity & {
      outcome: ThirdPartyIntegrationOutcome;
    }
  >;

export type ConversationAutomationInternalNoteLogEntry = ActivityLogEntryBase<
  AutomationActivityType.AUTOMATION_INTERNAL_NOTE_NODE_VISITED,
  BaseAutomationActivity
>;

export type ConversationAutomationChatbotLogEntry = ActivityLogEntryBase<
  AutomationActivityType.AUTOMATION_CHATBOT_NODE_VISITED,
  BaseAutomationActivity & {
    chatbot_id?: string;
    chatbot_name?: string;
    exit_reason?: string;
  }
>;

export type ConversationAutomationChatbotSingleInteractionLogEntry =
  ActivityLogEntryBase<
    AutomationActivityType.AUTOMATION_CHATBOT_SINGLE_INTERACTION_NODE_VISITED,
    BaseAutomationActivity & {
      chatbot_id?: string;
      chatbot_name?: string;
      exit_reason?: string;
    }
  >;

export type ConversationAutomationAiAgentLogEntry = ActivityLogEntryBase<
  AutomationActivityType.AUTOMATION_AI_AGENT_NODE_VISITED,
  BaseAutomationActivity & {
    chatbot_id?: string;
    chatbot_name?: string;
    exit_reason?: string;
  }
>;

type AutomationCustomAttributesNodeOutcomeError = {
  entity_id: 'contact' | 'conversation';
  entity_instance_id: string;
  attribute_id: string;
  error: string;
};

export const isAutomationCustomAttributesNodeOutcomeError = (
  outcome: AutomationCustomAttributesNodeOutcome,
): outcome is AutomationCustomAttributesNodeOutcomeError =>
  (outcome as AutomationCustomAttributesNodeOutcomeError).error !== undefined;
type AutomationCustomAttributesNodeOutcomeSuccess = {
  entity_id: 'contact' | 'conversation';
  entity_instance_id: string;
  attribute_id: string;
  value: string | string[] | object[];
};
type AutomationCustomAttributesNodeOutcome =
  | AutomationCustomAttributesNodeOutcomeError
  | AutomationCustomAttributesNodeOutcomeSuccess;

export type AutomationCustomAttributesNodeVisitedLogEntry =
  ActivityLogEntryBase<
    | AutomationActivityType.AUTOMATION_CONVERSATION_CUSTOM_FIELDS_NODE_VISITED
    | AutomationActivityType.AUTOMATION_CONTACT_CUSTOM_FIELDS_NODE_VISITED,
    BaseAutomationActivity & {
      outcomes: AutomationCustomAttributesNodeOutcome[];
    }
  >;
/** Represents the automation actions performed on the conversation. */
export type ConversationAutomationLogEntry =
  | ConversationAutomationConditionLogEntry
  | ConversationAutomationWaitLogEntry
  | ConversationAutomationReplyLogEntry
  | ConversationAutomationTagLogEntry
  | ConversationAutomationQueueLogEntry
  | ConversationAutomationCloseLogEntry
  | ConversationAutomationStopLogEntry
  | ConversationAutomationBusinessHoursLogEntry
  | ConversationAutomationExternalIntegrationLogEntry
  | ConversationAutomationFinishedLogEntry
  | ConversationAutomationSideConversationLogEntry
  | ConversationAutomationThirdPartyIntegrationLogEntry
  | AutomationCustomAttributesNodeVisitedLogEntry
  | ConversationAutomationInternalNoteLogEntry
  | ConversationAutomationChatbotLogEntry
  | ConversationAutomationAiAgentLogEntry
  | ConversationAutomationChatbotSingleInteractionLogEntry;

type OutcomeFailedCall = {
  type: 'failed_call';
  response_code: number;
};

type OutcomeFailedToConstruct = {
  type: 'failed_to_construct';
  error_message: string;
};

type OutcomeSuccess = {
  type: 'success';
};

type OutcomeTimeout = {
  type: 'timeout';
  timeout_in_seconds: number;
};

type OutcomeFailedConnection = {
  type: 'failed_connection';
  error_message: string;
};

type Outcome =
  | OutcomeFailedCall
  | OutcomeFailedToConstruct
  | OutcomeSuccess
  | OutcomeTimeout
  | OutcomeFailedConnection;

type ThirdPartyIntegrationOutcome =
  | OutcomeSuccess
  | {
      type: 'error';
      message: string;
    };

/** Represents the conversation activity log model for automation. */
export type ConversationAutomationActivityLog = {
  /** The automation activity log entries. */
  readonly automation_activities: readonly ConversationAutomationLogEntry[];
};
